import { z } from 'zod';
import { auditFieldsSchema, idSchema, paginated, paginationQuerySchema } from './common.js';
/**
 * Agentes — o sistema falando com gente por WhatsApp.
 *
 * Esta primeira onda é só o encanamento: o canal configurado, o destinatário
 * cadastrado, a mensagem saindo e TUDO registrado. As rotinas automáticas
 * (contas a pagar da semana, toda segunda) vêm depois e usam isto por baixo.
 */
/* ================================ Canal ================================= */
export const MESSAGE_CHANNEL_KINDS = ['WHATSAPP_CLOUD'];
export const messageChannelKindSchema = z.enum(MESSAGE_CHANNEL_KINDS);
export const MESSAGE_CHANNEL_KIND_LABELS = {
    WHATSAPP_CLOUD: 'WhatsApp (API oficial da Meta)',
};
/**
 * Telefone no formato que a Meta usa: só dígitos, com o código do país e SEM
 * o `+`. Brasil dá 13 (55 + DDD + 9 dígitos) ou 12 no fixo.
 *
 * O `phoneSchema` do cadastro NÃO serve aqui: ele é o telefone brasileiro sem
 * DDI, e mandar `68999331020` para a Meta entrega a mensagem em outro país ou
 * em lugar nenhum.
 */
export const whatsappPhoneSchema = z
    .string()
    .trim()
    .transform((value) => value.replace(/\D/g, ''))
    .refine((value) => value.length >= 10 && value.length <= 15, {
    message: 'Informe o número com código do país (ex.: 5568999331020)',
});
export const messageChannelSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    kind: messageChannelKindSchema,
    name: z.string(),
    displayPhone: z.string(),
    phoneNumberId: z.string(),
    wabaId: z.string(),
    /// O token NUNCA volta pela API. Isto diz só se existe um guardado.
    hasToken: z.boolean(),
    /// Últimos 4 dígitos, para o operador reconhecer qual token está lá.
    tokenHint: z.string().nullable(),
    /// Sem o App Secret o servidor não tem como provar que o webhook veio mesmo
    /// da Meta — e então recusa tudo que chega.
    hasAppSecret: z.boolean(),
    verifyToken: z.string(),
    webhookUrl: z.string(),
    isActive: z.boolean(),
    lastCheckAt: z.string().nullable(),
    lastCheckOk: z.boolean().nullable(),
    lastCheckError: z.string().nullable(),
});
export const messageChannelInputSchema = z.object({
    kind: messageChannelKindSchema.default('WHATSAPP_CLOUD'),
    name: z.string().trim().min(2, 'Dê um nome ao canal').max(80),
    displayPhone: whatsappPhoneSchema,
    phoneNumberId: z.string().trim().min(5, 'Informe o Phone Number ID').max(64),
    wabaId: z.string().trim().min(5, 'Informe o WABA ID').max(64),
    /**
     * Token permanente do System User. Ao ATUALIZAR, deixar vazio mantém o que
     * já está guardado — reexibir o token para reenviá-lo seria o mesmo que
     * publicá-lo na tela.
     */
    accessToken: z.string().trim().max(1000).optional(),
    /**
     * App Secret do aplicativo na Meta. É com ele que se confere a assinatura
     * de cada webhook — sem isso, qualquer um que descubra a URL manda
     * "mensagem recebida" forjada, e na Onda 3 isso vira aprovação de pagamento.
     * Mesma regra do token: vazio na atualização mantém o guardado.
     */
    appSecret: z.string().trim().max(500).optional(),
    /// Segredo do handshake do webhook. Vazio: o servidor sorteia um.
    verifyToken: z.string().trim().max(120).optional(),
    isActive: z.boolean().default(true),
});
/* ============================= Destinatário ============================== */
export const messageRecipientSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    channelId: idSchema,
    name: z.string(),
    phone: z.string(),
    /// Onda 3: quem responder por este número age COMO este usuário, com as
    /// permissões dele. Nulo aqui significa que só recebe, não comanda.
    userId: idSchema.nullable(),
    userName: z.string().nullable(),
    isActive: z.boolean(),
});
export const messageRecipientInputSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome de quem recebe').max(120),
    phone: whatsappPhoneSchema,
    userId: idSchema.optional().nullable(),
    isActive: z.boolean().default(true),
});
/* =============================== Mensagem =============================== */
export const MESSAGE_DIRECTIONS = ['SAIDA', 'ENTRADA'];
export const messageDirectionSchema = z.enum(MESSAGE_DIRECTIONS);
export const MESSAGE_DIRECTION_LABELS = {
    SAIDA: 'Enviada',
    ENTRADA: 'Recebida',
};
export const MESSAGE_STATUSES = ['PENDENTE', 'ENVIADA', 'ENTREGUE', 'LIDA', 'FALHOU'];
export const messageStatusSchema = z.enum(MESSAGE_STATUSES);
export const MESSAGE_STATUS_LABELS = {
    PENDENTE: 'Na fila',
    ENVIADA: 'Enviada',
    ENTREGUE: 'Entregue',
    LIDA: 'Lida',
    FALHOU: 'Falhou',
};
export const messageSchema = z.object({
    id: idSchema,
    companyId: idSchema,
    channelId: idSchema,
    direction: messageDirectionSchema,
    /// Telefone do outro lado: o destino na saída, o remetente na entrada.
    peerPhone: z.string(),
    peerName: z.string().nullable(),
    /// Nome do modelo aprovado na Meta, quando a mensagem foi por template.
    templateName: z.string().nullable(),
    body: z.string(),
    status: messageStatusSchema,
    errorMessage: z.string().nullable(),
    sentAt: z.string().nullable(),
    deliveredAt: z.string().nullable(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
export const messageListQuerySchema = paginationQuerySchema.extend({
    direction: messageDirectionSchema.optional(),
    status: messageStatusSchema.optional(),
});
export const messageListResponseSchema = paginated(messageSchema);
/**
 * Envio de teste.
 *
 * Duas formas porque a Meta tem duas regras: mensagem que a empresa começa do
 * nada exige TEMPLATE aprovado; texto livre só passa se a pessoa escreveu para
 * o número nas últimas 24 horas. A tela explica isso; aqui ficam as duas.
 */
export const messageTestInputSchema = z.object({
    recipientId: idSchema,
    mode: z.enum(['TEMPLATE', 'TEXTO']).default('TEMPLATE'),
    /// `hello_world` existe em toda conta nova da Meta — serve de teste sem
    /// esperar aprovação de modelo nenhum.
    templateName: z.string().trim().max(120).default('hello_world'),
    templateLanguage: z.string().trim().max(10).default('en_US'),
    text: z.string().trim().max(1000).optional(),
});
/** Resultado do "testar conexão": o canal responde e o token vale? */
export const channelCheckSchema = z.object({
    ok: z.boolean(),
    displayPhone: z.string().nullable(),
    verifiedName: z.string().nullable(),
    error: z.string().nullable(),
    checkedAt: z.string(),
});
//# sourceMappingURL=agentes.js.map